import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { ResolvedEndUserThemeSchema } from '../customer/end-user-theme.schema'

const c = initContract()

// Schema for flow status
const FlowStatusSchema = z.object({
  hasCompletedQuestionnaire: z.boolean(),
  hasCompletedHappinessCheck: z.boolean(),
})

// Schema for end user data
const EndUserSchema = z.object({
  id: z.string(),
  expired: z.boolean(),
  referral: z.boolean(),
  firstName: z.string().nullable(),
  middle: z.string().nullable().optional(),
  lastName: z.string().nullable(),
  referralOf: z.string().nullable().optional(),
  campaignId: z.string().optional(),
  flowStatus: FlowStatusSchema,
})

// Schema for incentive item
const IncentiveItemSchema = z.object({
  id: z.string(),
  campaignId: z.string().nullable(),
  customerId: z.string().nullable().optional(),
  name: z.string(),
  price: z.number().nullable(),
  description: z.string().nullable(),
  isAvailable: z.boolean(),
  picture: z.string().nullable(),
  skuCode: z.string().nullable(),
})

// Schema for campaign incentive
const CampaignIncentiveSchema = z.object({
  id: z.string(),
  referral: z.boolean().nullable(),
  incentiveItem: IncentiveItemSchema,
})

// Schema for customer
const CustomerSchema = z.object({
  id: z.string(),
  brand: z.string().nullable().optional(),
  // null only when the campaign has no customer. Every brand resolves a theme.
  endUserTheme: ResolvedEndUserThemeSchema.nullable(),
})

// Schema for campaign
const CampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  companyName: z.string().nullable().optional(),
  logo: z.string().nullable().optional(),
  salutation: z.any().nullable().optional(),
  details: z.any().nullable().optional(),
  referralCampaign: z.boolean().nullable(),
  questionnaire: z.any().nullable().optional(),
  referralQuestionnaire: z.any().nullable().optional(),
  happinessCheck: z.any().nullable().optional(),
  endDate: z.string().nullable().optional(),
  mainLandingText: z.any().nullable().optional(),
  referralLandingText: z.any().nullable().optional(),
  postcardTemplate: z.any().nullable().optional(),
  privacyLink: z.string().nullable().optional(),
  campaignIncentives: z.array(CampaignIncentiveSchema),
  campaignCategory: z.string().nullable().optional(),
  ecFlowTexts: z.any().nullable().optional(),
  languages: z.any().nullable().optional(),
  customer: CustomerSchema,
  endUserUrl: z.string().optional(),
})

// Schema for integration
const IntegrationSchema = z.object({
  features: z.any().nullable().optional(),
})

// Schema for end user with campaign response
const EndUserWithCampaignSchema = z.object({
  endUser: EndUserSchema,
  campaign: CampaignSchema,
  integrations: z.array(IntegrationSchema).optional(),
})

// Schema for event ID response
const EventIdResponseSchema = z.object({
  id: z.string(),
})

// Schema for set incentive input
const SetIncentiveInputSchema = z.object({
  id: z.string(),
  campaignIncentiveId: z.string(),
})

// Schema for questionnaire input
const QuestionnaireInputSchema = z.object({
  id: z.string(),
  questionnaireAnswers: z.any(),
})

// Schema for happiness check input
const HappinessCheckInputSchema = z.object({
  id: z.string(),
  happinessAnswers: z.any(),
})

// Schema for referral codes response
const ReferralCodeSchema = z.object({
  id: z.string(),
})

// Schema for generate referral code response
const GenerateReferralCodeResponseSchema = z.object({
  id: z.string(),
})

const prefix = '/v2/public'

export const endUserPublic = c.router({
  // Query: Get end user with campaign data
  endUserWithCampaign: {
    method: 'GET',
    path: `${prefix}/end-user/:userId`,
    pathParams: z.object({
      userId: z.string(),
    }),
    query: z.object({
      campaignId: z.string().optional(),
    }),
    responses: {
      200: EndUserWithCampaignSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get end user with campaign data (public endpoint)',
  },

  // Mutation: Track when end user reaches campaign
  reached: {
    method: 'POST',
    path: `${prefix}/end-user/:endUserId/reached`,
    pathParams: z.object({
      endUserId: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: EventIdResponseSchema,
      500: z.object({ message: z.string() }),
    },
    summary: 'Track when end user reaches campaign',
  },

  // Mutation: Track questionnaire start
  questionnaireStarted: {
    method: 'POST',
    path: `${prefix}/end-user/:endUserId/questionnaire-started`,
    pathParams: z.object({
      endUserId: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: EventIdResponseSchema,
      500: z.object({ message: z.string() }),
    },
    summary: 'Track when end user starts questionnaire',
  },

  // Mutation: Track referral code opened
  referralCodeOpened: {
    method: 'POST',
    path: `${prefix}/end-user/:endUserId/referral-code-opened`,
    pathParams: z.object({
      endUserId: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: EventIdResponseSchema,
      500: z.object({ message: z.string() }),
    },
    summary: 'Track when end user opens referral code',
  },

  // Mutation: Set incentive for end user
  setIncentive: {
    method: 'POST',
    path: `${prefix}/end-user/set-incentive`,
    body: SetIncentiveInputSchema,
    responses: {
      200: z.object({ aggregateId: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Set incentive for end user',
  },

  // Mutation: Save questionnaire answers
  saveEndUserQuestionnaire: {
    method: 'POST',
    path: `${prefix}/end-user/questionnaire`,
    body: QuestionnaireInputSchema,
    responses: {
      200: z.object({ aggregateId: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Save end user questionnaire answers',
  },

  // Mutation: Save happiness check
  saveEndUserHappinessCheck: {
    method: 'POST',
    path: `${prefix}/end-user/happiness-check`,
    body: HappinessCheckInputSchema,
    responses: {
      200: z.object({ aggregateId: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Save end user happiness check',
  },

  // Query: Get referral codes for user
  referralCodes: {
    method: 'GET',
    path: `${prefix}/end-user/:id/referral-codes`,
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: z.array(ReferralCodeSchema),
      500: z.object({ message: z.string() }),
    },
    summary: 'Get referral codes for end user',
  },

  // Mutation: Generate new referral code
  generateReferralCode: {
    method: 'POST',
    path: `${prefix}/end-user/:id/generate-referral-code`,
    pathParams: z.object({
      id: z.string(),
    }),
    body: z.object({}),
    responses: {
      200: GenerateReferralCodeResponseSchema,
      404: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary: 'Generate new referral code for end user',
  },
})

export {
  CampaignIncentiveSchema,
  CampaignSchema,
  CustomerSchema,
  EndUserSchema,
  EndUserWithCampaignSchema,
  FlowStatusSchema,
  IncentiveItemSchema,
  IntegrationSchema,
}
